import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { alterVoucher, createVoucher, type LedgerOption, type VoucherFull, type VoucherInput, type VoucherType } from '@/api/accountsBooks';

/** What every voucher form gets from VoucherFormModal. */
export interface FormProps {
  /** The voucher being altered, or null for a new one. */
  voucher: VoucherFull | null;
  ledgers: LedgerOption[];
  /** The number a new voucher of this type will get. */
  nextNo: string;
  onClose: () => void;
}

/** Saves (or alters) a voucher, refreshes the Accounts screens and closes the form. */
export function useSaveVoucher(type: VoucherType, voucher: VoucherFull | null, onClose: () => void) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: VoucherInput) => (voucher ? alterVoucher(voucher.id, body) : createVoucher(type, body)),
    onSuccess: (v) => {
      toast.push(`${type} ${v.voucherNo} ${voucher ? 'altered' : 'saved'}`, 'success');
      qc.invalidateQueries({ queryKey: ['acc-books'] });
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
}
